import { useState, useEffect, useCallback } from 'react';
import { Loader2, Save, AlertCircle, PlusCircle } from 'lucide-react';
import { useToast } from '../../../../components/toast/ToastProvider';
import { fetchMerchantConfig, createMerchantConfig } from '../../api/merchantConfigClient';

const VALUE_FIELD_BY_KEY = {
  MERCHANT_GENERAL_CONFIGURATIONS: 'merchant_general_configurations_value',
  MERCHANT_SHIPMENT_LABEL_CONFIGURATIONS: 'merchant_shipment_label_configurations_value',
  MERCHANT_SHIPMENT_INVOICE_CONFIGURATIONS: 'merchant_shipment_invoice_configurations_value',
  MERCHANT_ORDER_SUMMARY_REPORT_CONFIGURATIONS: 'merchant_order_summary_report_configurations_value',
  MERCHANT_SCHEDULED_EMAIL_REPORT_CONFIGURATIONS: 'merchant_scheduled_email_report_configurations_value',
  MERCHANT_COD_REMITTANCE_REPORT_CONFIGURATIONS: 'merchant_cod_remittance_configurations_value',
  MERCHANT_EXTERNAL_SHIPMENT_NOTES_CONFIGURATIONS: 'merchant_external_shipment_notes_configurations_value',
  MERCHANT_SMS_NOTIFICATION_CONFIGURATIONS: 'merchant_sms_notification_configurations_value',
  MERCHANT_WHATSAPP_NOTIFICATION_CONFIGURATIONS: 'merchant_whats_app_notification_configurations_value',
  MERCHANT_CREATE_ORDER_CONFIRMATION_CONFIGURATIONS: 'merchant_create_order_confirmation_configurations_value',
  MERCHANT_NDR_CUSTOMER_INTERACTION_CONFIGURATIONS: 'merchant_ndr_customer_interaction_configurations_value',
  MERCHANT_WEBHOOK_CONFIGURATIONS: 'merchant_webhook_configurations_value',
};

export function useConfigTab(merchantId, configKey, reloadKey) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [configValue, setConfigValue] = useState(null);
  const [metadata, setMetadata] = useState(null);
  const { showToast } = useToast();

  const loadConfig = useCallback(async () => {
    if (!merchantId) return;
    setLoading(true);
    setConfigValue(null);
    setMetadata(null);
    setIsNew(false);
    try {
      const raw = await fetchMerchantConfig({ merchantId, configKey });
      setMetadata({
        created_date_time: raw.created_date_time,
        updated_date_time: raw.updated_date_time,
        created_by: raw.created_by,
        updated_by: raw.updated_by,
      });
      const valueField = VALUE_FIELD_BY_KEY[configKey];
      const value = valueField ? raw[valueField] : null;
      if (value && typeof value === 'object' && !Array.isArray(value)) {
        setConfigValue(value);
      } else {
        setConfigValue(null);
      }
    } catch (e) {
      if (e?.isNotFound || e?.status === 404) {
        setIsNew(true);
        setConfigValue(null);
        setMetadata(null);
      } else {
        showToast(e?.error_message || e?.message || 'Failed to load config', 'error', 3000);
      }
    } finally {
      setLoading(false);
    }
  }, [merchantId, configKey, showToast]);

  useEffect(() => {
    if (merchantId) loadConfig();
  }, [merchantId, configKey, loadConfig, reloadKey]);

  const saveConfig = useCallback(async (body) => {
    if (!merchantId) return;
    setSaving(true);
    try {
      await createMerchantConfig({ merchantId, configKey, body });
      showToast('Configuration saved successfully', 'success', 2000);
      await loadConfig();
    } catch (e) {
      showToast(e?.error_message || e?.message || 'Failed to save', 'error', 3000);
    } finally {
      setSaving(false);
    }
  }, [merchantId, configKey, loadConfig, showToast]);

  return { loading, saving, isNew, configValue, metadata, saveConfig, reload: loadConfig };
}

export const FORM_STYLES = {
  section: 'space-y-4',
  sectionCard: 'border border-slate-200 rounded-xl p-3 sm:p-4 md:p-5 bg-white',
  sectionTitle: 'text-sm font-semibold text-slate-800 mb-3 flex items-center gap-2',
  label: 'block text-[11px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5',
  input: 'w-full px-3 py-2 sm:py-2.5 border border-slate-200 rounded-lg text-sm focus:border-blue-500 focus:ring-1 focus:ring-blue-500/30 bg-white transition-colors',
  inputDisabled: 'w-full px-3 py-2 sm:py-2.5 border border-slate-100 rounded-lg text-sm bg-slate-50 text-slate-500 cursor-not-allowed',
  toggle: 'relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2',
  toggleDot: 'inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow-sm',
  btnPrimary: 'inline-flex items-center justify-center gap-2 px-4 sm:px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg disabled:opacity-50 transition-colors shadow-sm w-full sm:w-auto touch-manipulation min-h-[44px]',
  btnSecondary: 'inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2 border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium rounded-lg transition-colors touch-manipulation',
  btnDanger: 'inline-flex items-center gap-1.5 px-3 py-1.5 text-red-600 hover:bg-red-50 text-sm rounded-lg transition-colors touch-manipulation min-h-[44px] sm:min-h-0',
};

export function Toggle({ checked, onChange, label, id, description, disabled = false }) {
  return (
    <div className="flex items-start gap-3">
      <button
        type="button"
        id={id}
        role="switch"
        aria-checked={checked}
        onClick={() => !disabled && onChange(!checked)}
        disabled={disabled}
        className={`${FORM_STYLES.toggle} ${checked ? 'bg-blue-600' : 'bg-slate-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} mt-0.5 shrink-0`}
      >
        <span className={`${FORM_STYLES.toggleDot} ${checked ? 'translate-x-5' : 'translate-x-1'}`} />
      </button>
      <div>
        <label htmlFor={id} className={`text-sm font-medium text-slate-700 ${disabled ? '' : 'cursor-pointer'}`}>{label}</label>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
    </div>
  );
}

export function TagListInput({ values, onChange, placeholder, disabled = false }) {
  const [input, setInput] = useState('');
  const list = values.filter(Boolean);

  const add = (item) => {
    const t = item.trim();
    if (!t || list.includes(t)) return;
    onChange([...list, t]);
    setInput('');
  };

  if (disabled) {
    return (
      <div className="flex flex-wrap gap-2 p-2.5 border border-slate-100 rounded-lg bg-slate-50 min-h-[42px]">
        {list.length === 0 && <span className="text-sm text-slate-400">-</span>}
        {list.map((item, i) => (
          <span key={i} className="inline-flex items-center px-2.5 py-1 rounded-md bg-slate-100 text-slate-600 text-sm border border-slate-200">
            {item}
          </span>
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-wrap gap-2 p-2.5 border border-slate-200 rounded-lg bg-white min-h-[42px] focus-within:ring-2 focus-within:ring-blue-500/30 focus-within:border-blue-400 transition-colors">
      {list.map((item, i) => (
        <span key={i} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-50 text-blue-800 text-sm border border-blue-100">
          {item}
          <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} className="text-blue-400 hover:text-red-600 p-0.5 transition-colors" aria-label="Remove">
            <span className="text-base leading-none">&times;</span>
          </button>
        </span>
      ))}
      <input
        type="text"
        value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(input); } }}
        onBlur={() => add(input)}
        placeholder={list.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[120px] border-0 outline-none text-sm bg-transparent placeholder:text-slate-400"
      />
    </div>
  );
}

export function SaveButton({ saving, onClick, label = 'Save Configuration' }) {
  return (
    <div className="pt-4 border-t border-slate-100">
      <button type="button" onClick={onClick} disabled={saving} className={FORM_STYLES.btnPrimary}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
        {saving ? 'Saving…' : label}
      </button>
    </div>
  );
}

export function EmptyConfigState({ configLabel, onAdd, saving }) {
  return (
    <div className="rounded-xl border-2 border-dashed border-slate-200 bg-gradient-to-br from-slate-50 to-white p-8 sm:p-12 text-center">
      <div className="w-16 h-16 bg-blue-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
        <AlertCircle className="w-8 h-8 text-blue-400" />
      </div>
      <h3 className="text-lg font-semibold text-slate-800 mb-2">No Configuration Found</h3>
      <p className="text-sm text-slate-500 max-w-md mx-auto mb-6">
        This configuration hasn't been set up yet for your account. Create a new configuration to get started.
      </p>
      <button type="button" onClick={onAdd} disabled={saving} className={FORM_STYLES.btnPrimary}>
        {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
        Create Configuration
      </button>
    </div>
  );
}

export function ConfigTimestamps({ metadata }) {
  if (!metadata) return null;
  const format = (dt) => {
    if (!dt) return '-';
    try {
      return new Intl.DateTimeFormat('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' }).format(new Date(dt));
    } catch { return dt; }
  };
  return (
    <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-400 mt-1">
      {metadata.created_date_time && (
        <span>Created: <span className="text-slate-500">{format(metadata.created_date_time)}</span>{metadata.created_by && <> by <span className="text-slate-500">{metadata.created_by}</span></>}</span>
      )}
      {metadata.updated_date_time && (
        <span>Updated: <span className="text-slate-500">{format(metadata.updated_date_time)}</span>{metadata.updated_by && <> by <span className="text-slate-500">{metadata.updated_by}</span></>}</span>
      )}
    </div>
  );
}

export function LoadingState() {
  return (
    <div className="py-16 flex flex-col items-center justify-center">
      <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      <p className="mt-3 text-sm text-slate-500">Loading configuration…</p>
    </div>
  );
}
